/**
 * API 调用客户端
 * 与 PassKeeper Workers API 通信
 */

import type {
  ApiResponse,
  PasswordItem,
  User,
  PasswordCategory,
} from "@passkeeper/shared";

const API_BASE = import.meta.env.DEV ? "" : "https://passkeeper-api.hulkcheng0806.workers.dev";

/** 存储 token */
let _token: string | null = null;

export function setToken(token: string | null) {
  _token = token;
  if (token) {
    localStorage.setItem("passkeeper_token", token);
  } else {
    localStorage.removeItem("passkeeper_token");
  }
}

export function getToken(): string | null {
  if (!_token) {
    _token = localStorage.getItem("passkeeper_token");
  }
  return _token;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });
    const data = await res.json();
    return data as ApiResponse<T>;
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "网络请求失败",
    };
  }
}

// ===== 认证 =====

export interface AuthResult {
  userId: string;
  email: string;
  token: string;
}

export async function register(
  email: string,
  masterPassword: string
): Promise<ApiResponse<AuthResult>> {
  return request<AuthResult>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, masterPassword }),
  });
}

export async function login(
  email: string,
  masterPassword: string
): Promise<ApiResponse<AuthResult>> {
  return request<AuthResult>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, masterPassword }),
  });
}

// ===== 密码条目 =====

export async function getItems(params?: {
  category?: PasswordCategory;
  q?: string;
}): Promise<ApiResponse<PasswordItem[]>> {
  const query = new URLSearchParams();
  if (params?.category) query.set("category", params.category);
  if (params?.q) query.set("q", params.q);
  const qs = query.toString();
  return request<PasswordItem[]>(`/api/items${qs ? `?${qs}` : ""}`);
}

export async function getItemById(
  id: string
): Promise<ApiResponse<PasswordItem>> {
  return request<PasswordItem>(`/api/items/${id}`);
}

export async function createItem(
  item: Partial<PasswordItem> & {
    url?: string;
    username?: string;
    password?: string;
    encData?: string;
    salt?: string;
    iv?: string;
  }
): Promise<ApiResponse<PasswordItem>> {
  return request<PasswordItem>("/api/items", {
    method: "POST",
    body: JSON.stringify(item),
  });
}

export async function updateItem(
  id: string,
  item: Partial<PasswordItem> & {
    url?: string;
    username?: string;
    password?: string;
    encData?: string;
    salt?: string;
    iv?: string;
  }
): Promise<ApiResponse<PasswordItem>> {
  return request<PasswordItem>(`/api/items/${id}`, {
    method: "PUT",
    body: JSON.stringify(item),
  });
}

export async function deleteItemById(
  id: string
): Promise<ApiResponse<null>> {
  return request<null>(`/api/items/${id}`, { method: "DELETE" });
}

export async function syncItems(
  entries: Array<{
    url?: string;
    username?: string;
    password?: string;
    title?: string;
    encData?: string;
    salt?: string;
    iv?: string;
  }>
): Promise<
  ApiResponse<
    Array<{
      url: string;
      status: "created" | "exists" | "error";
      id?: string;
    }>
  >
> {
  return request("/api/items/sync", {
    method: "POST",
    body: JSON.stringify({ entries }),
  });
}

export async function getDomains(): Promise<ApiResponse<string[]>> {
  return request<string[]>("/api/items/domains");
}

// ===== 健康检查 =====

export async function healthCheck(): Promise<ApiResponse<unknown>> {
  return request("/");
}